import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImagePlus, Palette } from 'lucide-react';
import { APPS } from '../apps';
import './Desktop.css';

const WALLPAPER_KEY = 'eg:desktop:wallpaper';

/** 预设壁纸：与桌面同一套深色渐变语言，第一条是原来的默认背景 */
const WALLPAPERS = [
  {
    id: 'midnight',
    name: '夜航',
    background:
      'radial-gradient(120% 90% at 12% 6%, #1a2340 0%, transparent 58%), ' +
      'radial-gradient(110% 85% at 88% 94%, #2a1e3d 0%, transparent 60%), ' +
      'linear-gradient(160deg, #0a0c14 0%, #070810 55%, #0b0a14 100%)',
  },
  {
    id: 'aurora',
    name: '极光',
    background:
      'radial-gradient(110% 80% at 20% 0%, #123a33 0%, transparent 55%), ' +
      'radial-gradient(120% 90% at 85% 100%, #1b2b52 0%, transparent 60%), ' +
      'linear-gradient(160deg, #071010 0%, #06090f 60%, #081012 100%)',
  },
  {
    id: 'dusk',
    name: '落霞',
    background:
      'radial-gradient(120% 90% at 15% 90%, #3d1f2e 0%, transparent 58%), ' +
      'radial-gradient(110% 80% at 85% 10%, #402a17 0%, transparent 55%), ' +
      'linear-gradient(160deg, #0d0a10 0%, #0a0810 55%, #100b0e 100%)',
  },
  {
    id: 'ink',
    name: '墨色',
    background: 'linear-gradient(165deg, #101319 0%, #0a0c11 60%, #0d0f14 100%)',
  },
];

const DEFAULT_WALLPAPER = { id: 'midnight', data: null };

function readStoredWallpaper() {
  try {
    const saved = JSON.parse(localStorage.getItem(WALLPAPER_KEY) || '');
    if (saved.id === 'custom' && saved.data) return saved;
    if (WALLPAPERS.some((item) => item.id === saved.id)) return { id: saved.id, data: null };
  } catch {
    // 没存过或存储损坏，都回到默认
  }
  return DEFAULT_WALLPAPER;
}

/** 用户图片压到最长边 1600px 转 JPEG，避免 dataURL 撑爆 localStorage */
function fileToWallpaper(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      const context = canvas.getContext('2d');
      context.fillStyle = '#0a0c14'; // 透明 PNG 落在深色底上
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error());
    };
    image.src = objectUrl;
  });
}

function Desktop() {
  const navigate = useNavigate();
  const [wallpaper, setWallpaper] = useState(readStoredWallpaper);
  const [panelOpen, setPanelOpen] = useState(false);
  const [error, setError] = useState('');

  const isCustom = wallpaper.id === 'custom';

  // 当前生效的背景值：预设取表里的渐变，自定义图片上面再压一层暗色保证文字可读
  const activeBackground = isCustom
    ? `linear-gradient(rgba(5, 6, 12, 0.3), rgba(5, 6, 12, 0.42)), ` +
      `url("${wallpaper.data}") center / cover no-repeat`
    : WALLPAPERS.find((item) => item.id === wallpaper.id).background;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // 壁纸面板开着时先关面板，再退出桌面
      if (panelOpen) setPanelOpen(false);
      else navigate('/');
    };

    // 桌面层是固定全屏，锁住底层滚动
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [navigate, panelOpen]);

  // 点空白壁纸 = 退出桌面；点图标、壁纸面板由各自 stopPropagation 处理
  const closeOnBackdrop = (event) => {
    if (event.target === event.currentTarget) navigate('/');
  };

  const applyWallpaper = (next) => {
    setWallpaper(next);
    setError('');
    try {
      localStorage.setItem(WALLPAPER_KEY, JSON.stringify(next));
    } catch {
      setError('浏览器存储空间不足，这张图暂时存不下来。');
    }
  };

  const onFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // 允许重复选同一张图
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件。');
      return;
    }
    try {
      const data = await fileToWallpaper(file);
      applyWallpaper({ id: 'custom', data });
    } catch {
      setError('这张图片读取失败，换一张试试。');
    }
  };

  return (
    <div
      className="desktop"
      role="presentation"
      style={{ background: activeBackground }}
      onClick={closeOnBackdrop}
    >
      <div className="desktop__glow" aria-hidden="true" />

      <nav className="desktop__grid" aria-label="应用">
        {APPS.map((app, index) => {
          const Icon = app.icon;
          return (
            <button
              key={app.id}
              type="button"
              className="desktop__app"
              style={{ '--i': index, '--tint': app.tint }}
              onClick={() => navigate(app.path)}
            >
              <span className="desktop__tile">
                <Icon size={26} strokeWidth={1.7} aria-hidden="true" />
              </span>
              <span className="desktop__label">{app.name}</span>
            </button>
          );
        })}
      </nav>

      {/* 换壁纸入口：右下角，避开图标网格 */}
      <button
        type="button"
        className="desktop__wallpaper-btn"
        aria-label="更换壁纸"
        aria-expanded={panelOpen}
        onClick={(event) => {
          event.stopPropagation();
          setPanelOpen((open) => !open);
        }}
      >
        <Palette size={19} strokeWidth={1.7} aria-hidden="true" />
      </button>

      {panelOpen && (
        <div
          className="desktop__wallpaper-panel"
          role="dialog"
          aria-label="更换壁纸"
          onClick={(event) => event.stopPropagation()}
        >
          <p className="desktop__wallpaper-title">壁纸</p>
          <div className="desktop__swatches">
            {WALLPAPERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`desktop__swatch${wallpaper.id === item.id ? ' is-active' : ''}`}
                style={{ background: item.background }}
                aria-label={item.name}
                aria-pressed={wallpaper.id === item.id}
                title={item.name}
                onClick={() => applyWallpaper({ id: item.id, data: null })}
              />
            ))}
            {isCustom && (
              <button
                type="button"
                className="desktop__swatch is-active"
                style={{
                  backgroundImage: `url("${wallpaper.data}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                aria-label="自定义图片"
                aria-pressed="true"
                title="自定义图片"
              />
            )}
            <label className="desktop__swatch desktop__swatch--upload" title="上传图片">
              <ImagePlus size={18} strokeWidth={1.7} aria-hidden="true" />
              <input type="file" accept="image/*" hidden onChange={onFileChange} />
            </label>
          </div>
          {error && (
            <p className="desktop__wallpaper-error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default Desktop;
